import { debugLog } from './debugUtils';

/**
 * Traffic Source Detection Utility
 * Detects user traffic sources and captures UTM parameters for ConvertKit tagging
 */

// Cache for traffic source data to avoid repeated calculations
let cachedTrafficSource = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get UTM parameters from URL
 */
export const getUTMParameters = (url = null) => {
  try {
    const targetUrl =
      url || (typeof window !== 'undefined' ? window.location.href : '');
    if (!targetUrl) return {};

    const urlObj = new URL(targetUrl);
    const params = urlObj.searchParams;

    return {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content: params.get('utm_content') || null,
      utm_term: params.get('utm_term') || null,
    };
  } catch (error) {
    debugLog('Error extracting UTM parameters:', error);
    return {};
  }
};

/**
 * Detect if traffic is from Facebook/Meta ads
 */
const isMetaTraffic = (utmParams, referrer, url) => {
  // Check UTM source
  if (utmParams.utm_source) {
    const source = utmParams.utm_source.toLowerCase();
    if (['facebook', 'meta', 'instagram', 'fb'].includes(source)) {
      return true;
    }
  }

  // Check referrer
  if (referrer) {
    const referrerDomain = referrer.toLowerCase();
    if (
      referrerDomain.includes('facebook.com') ||
      referrerDomain.includes('instagram.com') ||
      referrerDomain.includes('meta.com')
    ) {
      return true;
    }
  }

  // Check for fbclid parameter (Facebook Click ID)
  try {
    const urlObj = new URL(url || window.location.href);
    if (urlObj.searchParams.has('fbclid')) {
      return true;
    }
  } catch (error) {
    // Ignore URL parsing errors
  }

  return false;
};

/**
 * Detect if traffic is from Google Ads
 */
const isGoogleAdsTraffic = (utmParams, referrer, url) => {
  // Check UTM source
  if (utmParams.utm_source) {
    const source = utmParams.utm_source.toLowerCase();
    if (['google', 'googleads', 'google-ads', 'adwords'].includes(source)) {
      return true;
    }
  }

  // Check UTM medium for paid search
  if (utmParams.utm_medium) {
    const medium = utmParams.utm_medium.toLowerCase();
    if (['cpc', 'ppc', 'paid-search', 'google-ads'].includes(medium)) {
      return true;
    }
  }

  // Check for gclid parameter (Google Click ID)
  try {
    const urlObj = new URL(url || window.location.href);
    if (urlObj.searchParams.has('gclid')) {
      return true;
    }
  } catch (error) {
    // Ignore URL parsing errors
  }

  return false;
};

/**
 * Detect if traffic is from organic search
 */
const isOrganicSearchTraffic = referrer => {
  if (!referrer) return false;

  const searchEngines = [
    'google.com',
    'bing.com',
    'yahoo.com',
    'duckduckgo.com',
    'baidu.com',
    'yandex.com',
    'search.yahoo.com',
    'search.aol.com',
  ];

  const referrerDomain = referrer.toLowerCase();
  return searchEngines.some(engine => referrerDomain.includes(engine));
};

/**
 * Detect if traffic is from social media (non-Meta)
 */
const isSocialTraffic = (utmParams, referrer) => {
  // Check UTM source for social platforms
  if (utmParams.utm_source) {
    const source = utmParams.utm_source.toLowerCase();
    const socialPlatforms = [
      'twitter',
      'linkedin',
      'tiktok',
      'youtube',
      'pinterest',
      'snapchat',
      'reddit',
      'tumblr',
      'whatsapp',
      'telegram',
    ];
    if (socialPlatforms.includes(source)) {
      return { platform: source, isSocial: true };
    }
  }

  // Check referrer for social platforms
  if (referrer) {
    const referrerDomain = referrer.toLowerCase();
    const socialDomains = [
      { domain: 'twitter.com', platform: 'twitter' },
      { domain: 'x.com', platform: 'twitter' },
      { domain: 't.co', platform: 'twitter' }, // Twitter URL shortener
      { domain: 'linkedin.com', platform: 'linkedin' },
      { domain: 'tiktok.com', platform: 'tiktok' },
      { domain: 'youtube.com', platform: 'youtube' },
      { domain: 'pinterest.com', platform: 'pinterest' },
      { domain: 'snapchat.com', platform: 'snapchat' },
      { domain: 'reddit.com', platform: 'reddit' },
      { domain: 'tumblr.com', platform: 'tumblr' },
    ];

    for (const social of socialDomains) {
      if (referrerDomain.includes(social.domain)) {
        return { platform: social.platform, isSocial: true };
      }
    }
  }

  return { platform: null, isSocial: false };
};

/**
 * Detect if traffic is from email campaigns
 */
const isEmailTraffic = utmParams => {
  if (utmParams.utm_source) {
    const source = utmParams.utm_source.toLowerCase();
    const emailSources = [
      'email',
      'newsletter',
      'mailchimp',
      'convertkit',
      'klaviyo',
    ];
    if (emailSources.includes(source)) {
      return true;
    }
  }

  if (utmParams.utm_medium) {
    const medium = utmParams.utm_medium.toLowerCase();
    if (['email', 'newsletter'].includes(medium)) {
      return true;
    }
  }

  return false;
};

/**
 * Main function to detect traffic source and generate ConvertKit tags
 * Uses dual-tag approach: Channel + Attribution (e.g. "Facebook" + "Paid Traffic")
 */
export const detectTrafficSource = (options = {}) => {
  const { forceRefresh = false, url = null, referrer = null } = options;

  // Return cached result if available and not forcing refresh
  if (!forceRefresh && cachedTrafficSource && cacheTimestamp) {
    const isExpired = Date.now() - cacheTimestamp > CACHE_DURATION;
    if (!isExpired) {
      debugLog('🎯 Using cached traffic source:', cachedTrafficSource);
      return cachedTrafficSource;
    }
  }

  try {
    const currentUrl =
      url || (typeof window !== 'undefined' ? window.location.href : '');
    const currentReferrer =
      referrer || (typeof document !== 'undefined' ? document.referrer : '');

    // Validate URL if provided
    if (url && typeof url === 'string') {
      try {
        new URL(url);
      } catch (urlError) {
        debugLog('❌ Invalid URL provided:', url, urlError.message);
        throw new Error(`Invalid URL: ${url}`);
      }
    }

    // Get UTM parameters
    const utmParams = getUTMParameters(currentUrl);

    // Detect traffic source
    let trafficSource = 'direct';
    let sourceDetail = null;
    let channelTag = null;
    let attributionTag = null;
    const additionalTags = [];

    // 1. Check for Meta/Facebook traffic
    if (isMetaTraffic(utmParams, currentReferrer, currentUrl)) {
      channelTag = 'Facebook';

      // Determine if it's paid or organic Facebook traffic
      // If they have UTM source=facebook, it's usually a tracked campaign (paid)
      // fbclid parameter always indicates Facebook ads
      // Only consider it organic if it's just a Facebook referrer with no UTM tracking
      const isPaidFacebook =
        currentUrl.includes('fbclid=') ||
        (utmParams.utm_source &&
          ['facebook', 'meta'].includes(utmParams.utm_source.toLowerCase())) ||
        (utmParams.utm_medium &&
          ['cpc', 'paid', 'ad'].includes(utmParams.utm_medium.toLowerCase()));

      if (isPaidFacebook) {
        trafficSource = 'meta_ads';
        sourceDetail = 'facebook_paid';
        attributionTag = 'Paid Traffic';
      } else {
        trafficSource = 'facebook_organic';
        sourceDetail = 'facebook_organic';
        attributionTag = 'Organic Traffic';
      }

      // Add specific Meta campaign tags if available
      if (utmParams.utm_campaign) {
        additionalTags.push(`Meta Campaign: ${utmParams.utm_campaign}`);
      }
    }
    // 2. Check for Google Ads traffic
    else if (isGoogleAdsTraffic(utmParams, currentReferrer, currentUrl)) {
      trafficSource = 'google_ads';
      sourceDetail = 'paid_search';
      channelTag = 'Google';
      attributionTag = 'Paid Traffic';

      if (utmParams.utm_campaign) {
        additionalTags.push(`Google Campaign: ${utmParams.utm_campaign}`);
      }
    }
    // 3. Check for organic search traffic
    else if (isOrganicSearchTraffic(currentReferrer)) {
      trafficSource = 'organic_search';
      sourceDetail = 'search_engine';
      attributionTag = 'Organic Traffic';

      // Determine which search engine
      const referrerLower = currentReferrer.toLowerCase();
      if (referrerLower.includes('google.com')) {
        channelTag = 'Google';
      } else if (referrerLower.includes('bing.com')) {
        channelTag = 'Bing';
      } else if (referrerLower.includes('yahoo.com')) {
        channelTag = 'Yahoo';
      } else if (referrerLower.includes('duckduckgo.com')) {
        channelTag = 'DuckDuckGo';
      } else {
        channelTag = 'Search Engine';
      }
    }
    // 4. Check for email traffic
    else if (isEmailTraffic(utmParams)) {
      trafficSource = 'email';
      sourceDetail = utmParams.utm_source || 'email_campaign';
      channelTag = 'Purchase Source: Email';
      attributionTag = 'Organic Traffic';

      if (utmParams.utm_campaign) {
        additionalTags.push(`Purchase Campaign: ${utmParams.utm_campaign}`);
      }
    }
    // 5. Check for social traffic (non-Meta)
    else {
      const socialCheck = isSocialTraffic(utmParams, currentReferrer);
      if (socialCheck.isSocial) {
        trafficSource = 'social';
        sourceDetail = socialCheck.platform;
        channelTag =
          socialCheck.platform.charAt(0).toUpperCase() +
          socialCheck.platform.slice(1);
        attributionTag = 'Organic Traffic';
      }
      // 6. Check for referral traffic
      else if (
        currentReferrer &&
        (typeof window === 'undefined' ||
          !currentReferrer.includes(window.location.hostname))
      ) {
        trafficSource = 'referral';
        try {
          const referrerDomain = new URL(currentReferrer).hostname;
          sourceDetail = referrerDomain;
          channelTag = 'Referral';
          attributionTag = 'Organic Traffic';
          additionalTags.push(`Referral Domain: ${referrerDomain}`);
        } catch {
          channelTag = 'Referral';
          attributionTag = 'Organic Traffic';
        }
      }
      // 7. Default to direct traffic
      else {
        trafficSource = 'direct';
        sourceDetail = 'direct_visit';
        channelTag = 'Direct';
        attributionTag = 'Organic Traffic';
      }
    }

    // Build the tags array with channel + attribution
    const tags = [channelTag, attributionTag, ...additionalTags];

    // Add UTM-based tags for better attribution
    if (
      utmParams.utm_source &&
      utmParams.utm_source !== utmParams.utm_campaign
    ) {
      tags.push(`Purchase UTM Source: ${utmParams.utm_source}`);
    }

    if (utmParams.utm_medium) {
      tags.push(`Purchase UTM Medium: ${utmParams.utm_medium}`);
    }

    // Create result object
    const result = {
      trafficSource,
      sourceDetail,
      channelTag,
      attributionTag,
      tags,
      utmParams,
      timestamp: Date.now(),
      referrer: currentReferrer || null,
      // Provide primary tag for simple use cases (channel tag)
      primaryTag: channelTag || 'Direct',
    };

    // Cache the result
    cachedTrafficSource = result;
    cacheTimestamp = Date.now();

    debugLog('🎯 Traffic source detected:', result);
    return result;
  } catch (error) {
    debugLog('❌ Error detecting traffic source:', error);

    // Return fallback result
    const fallbackResult = {
      trafficSource: 'unknown',
      sourceDetail: 'detection_error',
      channelTag: 'Unknown',
      attributionTag: 'Organic Traffic',
      tags: ['Unknown', 'Organic Traffic'],
      utmParams: {},
      timestamp: Date.now(),
      referrer: null,
      primaryTag: 'Unknown',
    };

    cachedTrafficSource = fallbackResult;
    cacheTimestamp = Date.now();

    return fallbackResult;
  }
};

/**
 * Get just the ConvertKit tags for easy integration
 */
export const getConvertKitTags = (options = {}) => {
  const trafficData = detectTrafficSource(options);
  return trafficData.tags;
};

/**
 * Clear cached traffic source (useful for testing or forced refresh)
 */
export const clearTrafficSourceCache = () => {
  cachedTrafficSource = null;
  cacheTimestamp = null;
  debugLog('🗑️ Traffic source cache cleared');
};

/**
 * Get a simple traffic source string for logging/analytics
 */
export const getSimpleTrafficSource = (options = {}) => {
  const trafficData = detectTrafficSource(options);
  return trafficData.trafficSource;
};
